import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';
import App from './App';
import { ScrollToTop } from './components/scroll-to-top';
// Direct imports instead of barrel file to improve tree-shaking and prevent unused modals from being bundled
import { CreateLinkModal } from './components/modals/create-link-modal';
import { QRCodeModal } from './components/modals/qr-code-modal';
import { DeleteConfirmationModal } from './components/modals/delete-confirmation-modal';
import { ImportLinksModal } from './components/modals/import-links-modal';
import { ShareModal } from './components/modals/share-modal';
import { UrlChangeConfirmationModal } from './components/modals/url-change-confirmation-modal';
import './index.css';

// =============================================================================
// Referral Tracking
// =============================================================================

// ⚡ Performance Optimization: Defer referral tracking until browser is idle
// This removes blocking localStorage operation from critical path
// Referral codes are still captured, just not blocking the main thread
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    import('./lib/referral-tracking').then(({ captureReferralFromUrl }) => {
      captureReferralFromUrl();
    });
  });
} else {
  // Fallback for browsers without requestIdleCallback (e.g., Safari)
  setTimeout(() => {
    import('./lib/referral-tracking').then(({ captureReferralFromUrl }) => {
      captureReferralFromUrl();
    });
  }, 100);
}

// =============================================================================
// Sentry Error Monitoring
// =============================================================================

// ⚡ Performance Optimization: Defer Sentry initialization until browser is idle
// This reduces main thread blocking by ~150ms, improving Time to Interactive (TTI)
// ErrorBoundary below still works immediately, full Sentry features load after idle
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN_FRONTEND) {
  const initSentry = () => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN_FRONTEND,
      environment: 'production',
      enabled: true,
      // Sample 10% of transactions for performance monitoring
      tracesSampleRate: 0.1,
      // Capture 100% of errors
      sampleRate: 1.0,
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Session replay sample rates
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  };

  // Defer until browser is idle (non-blocking)
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initSentry);
  } else {
    // Fallback for browsers without requestIdleCallback (e.g., Safari)
    setTimeout(initSentry, 1000);
  }
}

// =============================================================================
// Query Client
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// =============================================================================
// App Root
// =============================================================================

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full rounded-2xl border bg-card p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg
                className="h-8 w-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-6">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred. Our team has been notified.'}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={resetError}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => (window.location.href = '/')}
                className="px-4 py-2 rounded-lg border font-medium hover:bg-muted transition-colors"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <NuqsAdapter>
            <App />
            {/* Global Modals */}
            <CreateLinkModal />
            <QRCodeModal />
            <DeleteConfirmationModal />
            <ImportLinksModal />
            <ShareModal />
            <UrlChangeConfirmationModal />
            <Toaster position="top-right" richColors />
          </NuqsAdapter>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
